import { Badge, Button, Card, Grid, Group, Modal, NavLink, NumberInput, ScrollArea, Stack, Switch, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { http } from "../../api/http";
import { DataTable } from "../../components/DataTable";
import { DynamicFields, type FieldDef } from "../../components/DynamicFields";
import { FieldsEditor } from "../../components/FieldsEditor";
import { PageHeader } from "../../components/ui";
import { useT } from "../../i18n/script";
import AdminLayout from "./AdminLayout";

interface Codelist { code: string; name: string; description?: string; group_name: string; parent_codelist?: string; fields: FieldDef[]; system: boolean; editable: boolean; item_count: number }
interface Item { id: number; code: string; name: string; parent_code?: string; sort: number; active: boolean; attributes: Record<string, unknown>; updated_by?: string }

function ItemModal({ cl, item, onClose }: { cl: Codelist; item: Item | null; onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [v, setV] = useState({ code: item?.code ?? "", name: item?.name ?? "", parent_code: item?.parent_code ?? "", sort: item?.sort ?? 100, active: item?.active ?? true,
    attributes: item?.attributes ?? {} });
  const m = useMutation({
    mutationFn: () => (item ? http.put(`/api/v1/codelists/${cl.code}/items/${item.id}`, { ...v, parent_code: v.parent_code || null })
      : http.post(`/api/v1/codelists/${cl.code}/items`, { ...v, parent_code: v.parent_code || null })),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cl-items"] }); qc.invalidateQueries({ queryKey: ["codelist"] }); onClose(); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  return (
    <Modal opened onClose={onClose} title={`${cl.name}: ${item ? item.name : t("nova stavka")}`} size="lg">
      <Stack>
        <Group grow>
          <TextInput label={t("Šifra")} required disabled={!!item} value={v.code} onChange={(e) => setV({ ...v, code: e.currentTarget.value })} />
          <TextInput label={t("Naziv")} required value={v.name} onChange={(e) => setV({ ...v, name: e.currentTarget.value })} />
        </Group>
        <Group grow>
          {cl.parent_codelist && <TextInput label={`${t("Nadređena stavka")} (${cl.parent_codelist})`} value={v.parent_code} onChange={(e) => setV({ ...v, parent_code: e.currentTarget.value })} />}
          <NumberInput label={t("Redoslijed")} value={v.sort} onChange={(x) => setV({ ...v, sort: Number(x) || 100 })} />
          <Switch mt="lg" label={t("Aktivna")} checked={v.active} onChange={(e) => setV({ ...v, active: e.currentTarget.checked })} />
        </Group>
        {cl.fields.length > 0 && <DynamicFields defs={cl.fields} values={v.attributes} onChange={(a) => setV({ ...v, attributes: a })} />}
        <Group justify="flex-end"><Button onClick={() => m.mutate()} loading={m.isPending}>{t("Sačuvaj")}</Button></Group>
      </Stack>
    </Modal>
  );
}

function NewCodelist({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [v, setV] = useState({ code: "", name: "", description: "", group_name: "Korisnički", fields: [] as FieldDef[] });
  const m = useMutation({ mutationFn: () => http.post("/api/v1/codelists", v),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["codelists"] }); onClose(); }, onError: (e: Error) => notifications.show({ color: "red", message: e.message }) });
  return (
    <Modal opened onClose={onClose} title={t("Novi šifarnik")} size="xl">
      <Stack>
        <Group grow><TextInput label={t("Kod (mala slova, _)")} value={v.code} onChange={(e) => setV({ ...v, code: e.currentTarget.value })} />
          <TextInput label={t("Naziv")} value={v.name} onChange={(e) => setV({ ...v, name: e.currentTarget.value })} />
          <TextInput label={t("Grupa")} value={v.group_name} onChange={(e) => setV({ ...v, group_name: e.currentTarget.value })} /></Group>
        <TextInput label={t("Opis")} value={v.description} onChange={(e) => setV({ ...v, description: e.currentTarget.value })} />
        <Text size="sm" fw={600}>{t("Dodatna polja stavki")}</Text>
        <FieldsEditor fields={v.fields} onChange={(f) => setV({ ...v, fields: f })} />
        <Group justify="flex-end"><Button onClick={() => m.mutate()} loading={m.isPending}>{t("Kreiraj")}</Button></Group>
      </Stack>
    </Modal>
  );
}

export default function CodelistsPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const [sel, setSel] = useState<string | null>("vrsta_voca");
  const [edit, setEdit] = useState<Item | null | "new">(null);
  const [inactive, setInactive] = useState(false);
  const [newCl, setNewCl] = useState(false);
  const cls = useQuery({ queryKey: ["codelists"], queryFn: () => http.get<Codelist[]>("/api/v1/codelists") });
  const cl = cls.data?.find((c) => c.code === sel);
  const items = useQuery({ queryKey: ["cl-items", sel, inactive], enabled: !!sel, queryFn: () => http.get<Item[]>(`/api/v1/codelists/${sel}/items`, { include_inactive: inactive }) });
  const toggle = useMutation({ mutationFn: (i: Item) => http.post(`/api/v1/codelists/${sel}/items/${i.id}/deactivate`, undefined, { active: !i.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cl-items"] }) });
  const groups = Array.from(new Set((cls.data ?? []).map((c) => c.group_name)));
  return (
    <AdminLayout>
      <>
        <PageHeader title="Šifarnici" description="Referentni podaci sistema. Stavke se ne brišu fizički (deaktivacija) — istorijski upisi zadržavaju značenje."
          right={<Button variant="light" leftSection={<IconPlus size={14} />} onClick={() => setNewCl(true)}>{t("Novi šifarnik")}</Button>} />
        <Grid>
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Card withBorder p={4}><ScrollArea.Autosize mah={700}>
              {groups.map((g) => (
                <div key={g}>
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase" px="sm" pt="sm">{t(g)}</Text>
                  {(cls.data ?? []).filter((c) => c.group_name === g).map((c) => (
                    <NavLink key={c.code} label={c.name} active={sel === c.code} onClick={() => setSel(c.code)}
                      rightSection={<Badge size="xs" variant="light">{c.item_count}</Badge>} />
                  ))}
                </div>
              ))}
            </ScrollArea.Autosize></Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 9 }}>
            {cl && (
              <Stack>
                <Group justify="space-between">
                  <div><Title order={4}>{cl.name}</Title><Text size="xs" c="dimmed">{cl.code}{cl.parent_codelist ? ` · ${t("nadređeni")}: ${cl.parent_codelist}` : ""}{cl.system ? ` · ${t("sistemski")}` : ""}</Text></div>
                  <Group><Switch label={t("Prikaži neaktivne")} checked={inactive} onChange={(e) => setInactive(e.currentTarget.checked)} />
                    {cl.editable && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setEdit("new")}>{t("Nova stavka")}</Button>}</Group>
                </Group>
                <DataTable rows={(items.data ?? []) as unknown as Record<string, unknown>[]} exportName={`sifarnik_${cl.code}`} onRowClick={(r) => cl.editable && setEdit(r as unknown as Item)} columns={[
                  { key: "code", label: "Šifra", latin: true }, { key: "name", label: "Naziv" },
                  ...(cl.parent_codelist ? [{ key: "parent_code", label: "Nadređena" }] : []),
                  ...cl.fields.map((f) => ({ key: f.key, label: f.label, latin: /eppo|kod|code|oznaka/i.test(f.key), render: (r: Record<string, unknown>) => {
                    const v = (r.attributes as Record<string, unknown>)?.[f.key];
                    return v === undefined || v === null ? "—" : typeof v === "boolean" ? (v ? "da" : "ne") : String(v);
                  } })),
                  { key: "active", label: "Aktivna", render: (r) => <Switch size="xs" checked={!!r.active} disabled={!cl.editable}
                    onClick={(e) => e.stopPropagation()} onChange={() => toggle.mutate(r as unknown as Item)} /> },
                ]} />
              </Stack>
            )}
          </Grid.Col>
        </Grid>
        {edit && cl && <ItemModal cl={cl} item={edit === "new" ? null : edit} onClose={() => setEdit(null)} />}
        {newCl && <NewCodelist onClose={() => setNewCl(false)} />}
      </>
    </AdminLayout>
  );
}
